// ============================================================
// TargetPanel - Right panel: tabbed layout with Body Map,
// Stats & Defence, and Conditions.
// ============================================================
import { memo, useState, useEffect, useMemo } from 'react';
import clsx from '../utils/clsx';
import common from '../styles/common.module.css';
import CombatantGallery from './CombatantGallery';
import TargetIdentityHeader from './TargetIdentityHeader';
import TargetTemplatesMenu from './TargetTemplatesMenu';
import { useRosterContext } from '../contexts/RosterContext';
import styles from '../styles/TargetPanel.module.css';
import BodyMap, { TargetTraitRow, ConditionRow } from './BodyMap.jsx';
import InboundStrip from './InboundStrip';
import CombatantStatusTab from './CombatantStatusTab';
import WoundsReadout from './WoundsReadout';
import { TurnStrip } from './StatusBand';
import ConditionsSection from './ConditionsSection';
import { FrenzyChip } from './FrenzyChip';
import ExposuresSection, { ExposeButton } from './ExposuresSection';
import { sheetOnTeam, teamsIn } from '../engine/roster/team';
import TeamsDialog from './TeamsDialog';
import CombatantSheetTabs from './CombatantSheetTabs';
import TargetFrame from './TargetFrame';
import TargetScanHud from './TargetScanHud';
import ThreatCodex from './ThreatCodex';
import { useTargetPanelEdits } from './useTargetPanelEdits';
import {
  resolveApplyConditionModifiers,
  resolveConditionModOptions,
  resolveConditionsBlockSheetTests,
  resolveUnnaturalDosMode,
  resolveStackPolicy,
} from '../engine/settings/resolve';
import { blockedReactionTests } from '../engine/reactionAvailability';
import { useTargets } from '../contexts/TargetsContext';
import { useSession } from '../contexts/SessionContext';
import useSheetEditor from '../hooks/useSheetEditor';
import { useAttacker } from '../contexts/AttackerContext';
import useTargetFigureSheet from '../hooks/useTargetFigureSheet';

// The tabs speak SCANNER (v8.256.0): labels renamed to the auspex
// grammar; the KEYS and tab-<key> testids are byte-stable - every
// E2E locator addresses keys since v8.250.4, so a label is free to
// say what the instrument does.
const TABS = [
  { key: 'status', label: 'Scan' },
  { key: 'characteristics', label: 'Threat' },
  { key: 'defender', label: 'Defence' },
  { key: 'stats', label: 'Anatomy' },
  { key: 'gear', label: 'Wargear' },
];

/**
 * The right panel: the threat header, the target picker, the tab bar,
 * the scan (status) tab and the sheet tabs shared with the actor panel.
 * The attacker-side inputs (slot, fire mode, ...) feed the defence
 * probability on the sheet tabs.
 */
function TargetPanel({
  defencePending,
  slot,
  fireMode,
  isMelee,
  attackMod,
  rangeBand,
  hatred,
  rerollDamage,
  attackerCharStats,
  attackerConditions,
  onCycle,
}) {
  const tc = useTargets();
  const { currentTab, activeTab } = tc;
  const { isLocked, permissions } = useSession();
  const rules = useTargetRules(permissions);
  const { roster } = useRosterContext();
  const sourceCharacter = currentTab?.sourceCharacter || null;
  const target = currentTab?.target;
  const [panelTab, setPanelTab] = useState('status');
  const [pickerOpen, setPickerOpen] = useState(false);
  const codex = useCodexFlip(panelTab, setPanelTab, activeTab);
  const locked = isLocked('editTarget') || defencePending;
  const panelEdits = useTargetPanelEdits({
    target,
    activeTab,
    updateTab: tc.updateTab,
    locked,
    stackPolicy: rules.stackPolicy,
  });
  const { sheet, edits } = useTargetSheet(activeTab, panelTab, isLocked('editSheets') || defencePending, panelEdits);
  // Tests a condition forbids outright, resolved where the permissions
  // are. The leaves render the map; they do not re-derive the rule.
  const blockedTests = blockedReactionTests(target, {
    applyConditionMods: rules.applyConditionModifiers,
    condOptions: rules.condModOptions,
    blockSheetTests: resolveConditionsBlockSheetTests(permissions),
  });
  // ◀ ▶ step through the order rail, wrapping, exactly as Tab does - the
  // order on screen, not the roster's (v8.377.10). The whole point of them
  // is changing target without opening anything.
  const cycleTarget = (delta) => onCycle?.(delta);
  return (
    <>
      <TargetHeader {...{ tc, target, sourceCharacter, codex, pickerOpen, setPickerOpen, cycleTarget, isLocked }} />
      <TabBar panelTab={panelTab} onTab={setPanelTab} condCount={target?.conditions?.length || 0} />
      {currentTab && (
        <>
          {panelTab === 'status' && (
            <TargetScanTab {...{ tc, target, sourceCharacter, codex, locked, edits, rules, roster, isLocked }} />
          )}
          {/* === SHEET TABS (shared with the actor panel) === */}
          <TargetSheetTabs
            {...{ tc, panelTab, target, sourceCharacter, locked, rules, blockedTests, edits }}
            sheet={sheet}
            attacker={{
              slot,
              fireMode,
              isMelee,
              attackMod,
              rangeBand,
              hatred,
              rerollDamage,
              attackerCharStats,
              attackerConditions,
            }}
          />
        </>
      )}
    </>
  );
}

/**
 * The Anatomy tab's live sheet, and the panel's edits with the sheet's
 * flash folded in: what a sheet edit stripped is said on the Scan tab
 * too, where the conditions are.
 */
function useTargetSheet(activeTab, panelTab, locked, panelEdits) {
  const editor = useSheetEditor(activeTab, panelTab === 'stats');
  const edits = panelEdits.condFlash || !editor.flash ? panelEdits : { ...panelEdits, condFlash: editor.flash };
  return { sheet: { editor, locked }, edits };
}

/**
 * The sheet tabs shared with the actor panel (Threat, Defence, Anatomy,
 * Wargear). Declarative: one prop per roll, edit and ruling it takes.
 */
function TargetSheetTabs(props) {
  const { tc, panelTab, target, sourceCharacter, locked, rules, blockedTests, edits, attacker, sheet } = props;
  return (
    <CombatantSheetTabs
      tab={panelTab}
      target={target}
      sourceCharacter={sourceCharacter || null}
      locked={locked}
      tone="target"
      sheetEditor={sheet.editor}
      sheetLocked={sheet.locked}
      className={styles.tabContent}
      editorWrapClassName={styles.editorWrap}
      onRollCharacteristic={tc.rollCharacteristic}
      onRollSkill={tc.rollSkill}
      onRollPlainTarget={tc.rollPlainTarget}
      onRollDiceExpr={tc.rollDiceExpr}
      onOpposedCharacteristic={tc.rollOpposedCharacteristic}
      onOpposedSkill={tc.rollOpposedSkill}
      unnaturalOverrides={tc.unnaturalOverrides}
      onUnnaturalOverride={tc.onUnnaturalOverride}
      applyConditionModifiers={rules.applyConditionModifiers}
      condModOptions={rules.condModOptions}
      blockedTests={blockedTests}
      defenderCustomMods={target?.defenderCustomMods || []}
      onAddMod={tc.onAddDefenderMod}
      onEditMod={tc.onEditDefenderMod}
      onRemoveMod={tc.onRemoveDefenderMod}
      onToggleFrenzied={edits.toggleFrenzied}
      onSetStandingReaction={edits.setStandingReaction}
      slot={attacker.slot}
      fireMode={attacker.fireMode}
      isMelee={attacker.isMelee}
      attackMod={attacker.attackMod}
      rangeBand={attacker.rangeBand}
      hatred={attacker.hatred}
      rerollDamage={attacker.rerollDamage}
      attackerCharStats={attacker.attackerCharStats}
      attackerConditions={attacker.attackerConditions}
      unnaturalDosMode={rules.unnaturalDosMode}
      onTargetChange={edits.changeTarget}
      onEditArmour={tc.handleEditArmour}
      onReorderWeapons={edits.reorderWeapons}
    />
  );
}

/** The table's rulings this panel reads, resolved once per render. */
function useTargetRules(permissions) {
  const applyConditionModifiers = resolveApplyConditionModifiers(permissions);
  const condModOptions = resolveConditionModOptions(permissions);
  const unnaturalDosMode = resolveUnnaturalDosMode(permissions);
  // One options object for the ledger consumers (header + defence
  // rail) - memoised so their own useMemos survive unrelated renders.
  const ledgerOptions = useMemo(
    () => ({ applyConditionMods: applyConditionModifiers, condOptions: condModOptions, unnaturalDosMode }),
    [applyConditionModifiers, condModOptions, unnaturalDosMode],
  );
  return {
    applyConditionModifiers,
    condModOptions,
    unnaturalDosMode,
    stackPolicy: resolveStackPolicy(permissions),
    ledgerOptions,
  };
}

/**
 * The scan flip (v8.269.0): clicking the denomination shows the THREAT
 * CODEX in the map cell. Opening from another tab jumps to the Scan tab
 * first - the flip must be visible. A new target gets a fresh scan, not
 * someone else's codex.
 */
function useCodexFlip(panelTab, setPanelTab, activeTab) {
  const [open, setOpen] = useState(false);
  const toggle = () => {
    if (panelTab !== 'status') setPanelTab('status');
    setOpen((o) => !o);
  };
  useEffect(() => setOpen(false), [activeTab]);
  return { open, toggle };
}

/**
 * The THREAT ASSESSMENT header (v8.255.0): a sensor return, not a
 * nameplate. Forked from PanelIdentityHeader (ADR) - the auspex readout
 * lines made the two headers structurally different; the actor's header
 * is untouched. Targets are chosen by LOOKING at them (v7.14.0): a grid
 * of cards with health-tinted silhouettes, not a list of names.
 */
function TargetHeader({ tc, target, sourceCharacter, codex, pickerOpen, setPickerOpen, cycleTarget, isLocked }) {
  // The viewer's live map line to this target (v8.255.0) - render-only
  // per-attacker facts, never written back through updateTab.
  const { mapMetres, rangeBandFromMap } = useAttacker();
  const { roster, actorId, onSaveCombatantToLibrary, updateCombatantSheet } = useRosterContext();
  const [teamsOpen, setTeamsOpen] = useState(false);
  /** A team is a fact on the sheet: each move is one sheet edit per person. */
  const applyTeams = (plan) =>
    plan.forEach(({ id, team }) => updateCombatantSheet(id, (sheet) => sheetOnTeam(sheet, team)));
  const moveToTeam = (ids, team) => applyTeams(ids.map((id) => ({ id, team })));
  return (
    <>
      <TargetIdentityHeader
        name={target?.name || tc.currentTab?.label || 'No target'}
        target={target}
        sourceCharacter={sourceCharacter}
        codexOpen={codex.open}
        onToggleCodex={codex.toggle}
        onNameClick={() => setPickerOpen((o) => !o)}
        expanded={pickerOpen}
        onPrev={() => cycleTarget(-1)}
        onNext={() => cycleTarget(1)}
        mapMetres={mapMetres}
        rangeBandFromMap={rangeBandFromMap}
        trailing={
          !isLocked('targetTemplates') ? (
            <TargetTemplatesMenu
              open={tc.showTgtMenu}
              onToggle={() => tc.setShowTgtMenu(!tc.showTgtMenu)}
              templates={tc.tgtTemplates}
              onLoad={tc.handleLoadTgtTemplate}
              onDelete={tc.handleDeleteTgtTemplate}
              newName={tc.newTgtName}
              onNewName={tc.setNewTgtName}
              onSave={tc.handleSaveTgtTemplate}
            />
          ) : null
        }
      />
      {pickerOpen && (
        <CombatantGallery
          roster={roster}
          value={tc.activeTab}
          actorId={actorId}
          onSelect={(id) => tc.setActiveTab(id)}
          onClose={() => setPickerOpen(false)}
          allowManage={!isLocked('manageTabs')}
          onAdd={tc.handleNewTab}
          onRemove={tc.handleCloseTab}
          onSaveToLibrary={onSaveCombatantToLibrary}
          onChangeTeams={isLocked('editSheets') ? undefined : () => setTeamsOpen(true)}
        />
      )}
      {teamsOpen && (
        <TeamsDialog roster={roster} onMove={moveToTeam} onApply={applyTeams} onClose={() => setTeamsOpen(false)} />
      )}
    </>
  );
}

function TabBar({ panelTab, onTab, condCount }) {
  return (
    <div className={common.panelTabBar} role="tablist" aria-label="Target panel tabs">
      {TABS.map((t) => (
        <button
          key={t.key}
          role="tab"
          data-testid={`tab-${t.key}`}
          aria-selected={panelTab === t.key}
          className={clsx(common.panelTab, panelTab === t.key && common.panelTabActive)}
          onClick={() => onTab(t.key)}
        >
          {t.label}
          {t.key === 'status' && condCount > 0 && (
            <span style={{ marginLeft: 4, fontSize: 9, color: 'var(--danger)' }}>({condCount})</span>
          )}
        </button>
      ))}
    </div>
  );
}

/**
 * The attacker's sights on this body (v8.243.0), memoised so BodyMap's
 * memo survives attacker churn that does not touch them. Attack lock
 * kills the rings; the armour lock is independent. NOTE: targetLocation
 * is LOCAL attacker state, so each viewer sees their own rings on a
 * shared target - correct, the v8.52.0 facing-chip precedent. The
 * re-render on attacker churn is accepted (ADR 2026-08-30).
 */
function useTargeting(isLocked) {
  const { attackToggles, targetLocation, calledShotReduction, engageCalledShot, clearCalledShot } = useAttacker();
  const attackLocked = isLocked('attack');
  const calledShotOn = !!attackToggles?.calledShot;
  return useMemo(
    () =>
      attackLocked
        ? null
        : {
            selectedId: targetLocation,
            calledShot: calledShotOn,
            penalty: Math.max(20 - (calledShotReduction || 0), 0),
            onEngageZone: engageCalledShot,
            onClear: clearCalledShot,
          },
    [attackLocked, targetLocation, calledShotOn, calledShotReduction, engageCalledShot, clearCalledShot],
  );
}

/** The Scan tab: body map (or codex), wounds, the traits and conditions rows, conditions and exposures. */
function TargetScanTab({ tc, target, sourceCharacter, codex, locked, edits, rules, roster, isLocked }) {
  const targeting = useTargeting(isLocked);
  const onRemove = locked ? undefined : edits.removeCondition;
  return (
    <CombatantStatusTab
      turnStrip={
        <TurnStrip tracker={tc.turnTrackerState} combatantId={tc.activeTab} onRerollInitiative={rerollFor(tc)} />
      }
      bodyMap={<ScanMap {...{ tc, target, sourceCharacter, codex, locked, targeting }} />}
      mapOverlay={<ScanOverlay target={target} codex={codex} ledgerOptions={rules.ledgerOptions} />}
      traitsRow={<TargetTraitRow target={target} sourceCharacter={sourceCharacter} />}
      conditionsRow={<ConditionRow target={target} onRemoveCondition={onRemove} />}
      wounds={<WoundsReadout target={target} locked={locked} onChange={edits.setHealth} />}
      footer={<ScanFooter target={target} locked={locked} edits={edits} roster={roster} />}
    />
  );
}

/** Re-roll THIS combatant's initiative, when the tracker offers it. */
function rerollFor(tc) {
  return tc.rerollInitiative ? () => tc.rerollInitiative(`tab-${tc.activeTab}`) : null;
}

/**
 * The map cell: the body map, or - the scan's other face (v8.270.0) -
 * the codex dataslate. Armour moved to RIGHT-click in v8.243.0; the left
 * click belongs to the sight rings.
 */
function ScanMap({ tc, target, sourceCharacter, codex, locked, targeting }) {
  const figureSheet = useTargetFigureSheet();
  if (codex.open) return <ThreatCodex target={target} sourceCharacter={sourceCharacter} onClose={codex.toggle} />;
  return (
    <BodyMap
      // One plate per combatant: a switch to another target is a new plate, whose impact (if one is live) is history.
      key={tc.activeTab}
      variant="fill"
      target={target}
      sourceCharacter={sourceCharacter}
      figureSheet={figureSheet}
      onLocationContextMenu={locked ? undefined : tc.handleEditArmour}
      targeting={targeting}
      impact={tc.impactsNow?.[tc.activeTab] ?? null}
      combatantId={tc.activeTab}
    />
  );
}

/**
 * The livery that breaks the actor/target symmetry (v8.244.0): the
 * frame's corners, always (the lock itself is the bracket on the plate,
 * ADR 2026-10-11). The instruments ride the frame (v8.262.0) and flip out
 * with the map; the codex is the other face.
 */
function ScanOverlay({ target, codex, ledgerOptions }) {
  return (
    <>
      <TargetFrame />
      {!codex.open && <TargetScanHud target={target} ledgerOptions={ledgerOptions} />}
    </>
  );
}

/**
 * What next round does to this body (v8.259.0, display only), the
 * conditions, and WHAT OTHERS SEE (v8.351.0): the exposures in their own
 * block - the same list, the same writers, filtered.
 */
function ScanFooter({ target, locked, edits, roster }) {
  return (
    <>
      <InboundStrip target={target} />
      <ConditionsSection
        target={target}
        locked={locked}
        flash={edits.condFlash}
        onAdd={edits.addCondition}
        onRemove={edits.removeCondition}
        onClear={edits.clearConditions}
        extraActions={
          <>
            <FrenzyChip frenzied={!!target.frenzied} onToggle={edits.toggleFrenzied} locked={locked} />
            <ExposeButton onAdd={edits.addCondition} locked={locked} teams={teamsIn(roster)} />
          </>
        }
      />
      <ExposuresSection
        target={target}
        locked={locked}
        onRemove={edits.removeCondition}
        onClear={edits.clearExposures}
      />
    </>
  );
}

export default memo(TargetPanel);
